<ui-section
	title="Lender Details"
	[subtitle]="subtitle"
	[subtitleClasses]="subtitleClasses"
	intercom="vtb-mortgage-lender-details"
>
	<ui-button
		title-action
		(click)="toggleLenderSelector()"
		label="Add"
		icon="plus"
		[disabled]="_addLenderDisabled"
		preset="MEDIUM_EMPHASIS"
		matTooltip="{{
			isVTBMortgage && _addLenderDisabled
				? 'Please add vendor details on the parties page.'
				: ''
		}}"
	/>
	<div section-content>
		<div class="flex gap-4 mb-3" *ngIf="!isVTBMortgage">
			<app-checkbox
				label="Acting for Mortgagee"
				type="clear"
				[ngModel]="_mortgage?.actingForMortgagee"
				(ngModelChange)="setActingForMortgagee($event)"
			></app-checkbox>
			<app-checkbox
				*ngIf="_mortgage?.source !== MatterMortgageInstructionSource.ASSYST"
				label="MMS mortgage"
				type="clear"
				[ngModel]="_mortgage?.mms"
				(ngModelChange)="setMMS($event)"
			></app-checkbox>
		</div>
		<div class="flex flex-col gap-y-6">
			<app-container type="clear" [border]="false" [padding]="false">
				<ng-container *ngIf="legacyPrivateLender && lenders?.length === 0">
					<div
						[matTooltip]="legacyPrivateLenderTooltip"
						matTooltipClass="wide-tooltip"
						matTooltipPosition="above"
					>
						<atomic-contact-card
							class="mt-5"
							[address]="legacyPrivateLender.address"
							[allowDelete]="true"
							[disabled]="true"
							[allowEdit]="false"
							(deleteContactEvent)="deleteLegacyPrivateLender($event)"
							[email]="legacyPrivateLender.email"
							[name]="legacyPrivateLender.fullName"
							[phone]="
								legacyPrivateLender.phone || legacyPrivateLender.workPhone
							"
						></atomic-contact-card>
					</div>
				</ng-container>
				<ng-container *ngIf="!legacyPrivateLender || lenders?.length > 0">
					<!-- Non VTB mortgage lender selector -->
					<ng-container *ngIf="!isVTBMortgage">
						<!-- Empty state -->
						<div
							*ngIf="!lenders?.length && !_showLenderSelector"
							class="my-6 rounded-xl bg-grey-veryLight w-full py-16 flex justify-center items-center"
						>
							<div class="flex flex-col">
								<img
									class="h-24 mb-2"
									src="assets/images/empty-states/multiple-lenders.svg"
								/>
								<app-button
									size="small"
									[icon]="ICON.ADD"
									btn="clear"
									class="bg-green text-white rounded-md"
									(click)="toggleLenderSelector()"
									>Add lender</app-button
								>
							</div>
						</div>

						<app-lender-selector
							class="mt-6"
							[mortgage]="_mortgage"
							(onSelectLender)="_onLenderAdded($event)"
							*ngIf="_showLenderSelector"
						></app-lender-selector>
					</ng-container>

					<!--VTB MORTGAGE LENDER SELECTOR - Section for vtb mortgages only (shows the file clients as possible lenders instead of a search input) -->
					<ng-container *ngIf="isVTBMortgage">
						<ng-container *ngIf="_showLenderSelector">
							<app-dropdown
								label="Add a vendor"
								(onSelect)="clientAsLenderDropdownSelect($event)"
								[itemGroups]="[
									{
										items: [{ text: searchValue, value: create }],
										template: addAllTemplate,
										hover: true,
									},
									{
										items: sellerDropdownOptions,
										noResultsMessage: 'No vendors found',
									},
								]"
							>
							</app-dropdown>

							<ng-template #addAllTemplate let-item>
								<div
									class="flex flex-1 justify-between"
									*ngIf="sellerDropdownOptions.length > 1"
								>
									<div class="flex gap-x-2 items-center">
										<div class="flex">
											<ng-container
												*ngFor="
													let sellerDropdownOption of sellerDropdownOptions;
													let i = index
												"
											>
												<atomic-name-badge
													style="z-index: {{
														sellerDropdownOptions.length - i
													}}"
													[ngClass]="{ '-ml-2': i > 0 }"
													size="small"
													[name]="sellerDropdownOption.name"
												>
												</atomic-name-badge>
											</ng-container>
										</div>
										<p>Add all {{ sellerDropdownOptions.length }} as lenders</p>
									</div>
								</div>
							</ng-template>
						</ng-container>
					</ng-container>

					<!-- VTB empty state when no clients are in file-->
					<div
						*ngIf="
							(vtbCapableClients$ | async).length === 0 &&
							isVTBMortgage &&
							lenders.length === 0
						"
					>
						<p>
							No vendors on file. Please
							<app-anchor [url]="addVendorUrl" type="static" class="inline"
								>click here</app-anchor
							>
							to add vendors.
						</p>
					</div>

					<ng-container
						*ngFor="let lender of lenders; trackBy: trackLenderById"
					>
						<app-lender-card
							class="mt-6"
							[(collapsed)]="collapsedCardStates[lender.id]"
							(collapsedChange)="onLenderCardExpanded($event, lender.id)"
							[collapsable]="lenders?.length > 1"
							[highlightOnExpand]="!collapsedCardStates[lender.id]"
							[lender]="lender"
							[primary]="lender.primary"
							(onEditLender)="onLenderUpdated.emit($event)"
							(onRemoveLender)="_onLenderRemoved(lender.id)"
							(onAddAssociation)="onAddAssociation($event)"
							(onRemoveInvestor)="onRemoveInvestor(lender.id, $event)"
							(onSetAsPrimary)="onLenderSetAsPrimary.emit($event)"
						></app-lender-card>
					</ng-container>
				</ng-container>
			</app-container>

			<app-closer-sync-mortgage-funder
				*ngIf="
					(_mortgage?.lenders | keyvalue)?.[0]?.value?.requestForFundsContact
						?.isProvidedViaInstructions
				"
				[mortgage]="_mortgage"
			></app-closer-sync-mortgage-funder>

			<app-container
				type="clear"
				[border]="false"
				[padding]="false"
				*ngIf="(_mortgage?.lenders | keyvalue)?.length > 0"
			>
				<div class="">
					<div class="flex my-4">
						<h5 class="mb-0 font-bold">Mortgage RE line</h5>
						<app-button
							class="ml-4"
							btn="border"
							size="small"
							[icon]="ICON.EDIT"
							(click)="editMortgageReLine()"
							>Edit text</app-button
						>
					</div>
				</div>
			</app-container>

			<app-container
				type="clear"
				[border]="false"
				[padding]="false"
				*ngIf="lenders?.length > 1"
			>
				<div class="flex flex-col">
					<h5 class="mb-2 font-bold">Percentage of Interest</h5>
				</div>

				<app-dropdown
					placeholder="N/A"
					(onSelect)="onPercentageInterestTypeSelected($event)"
					[itemGroups]="[{ items: percentageOfInterestOptions }]"
					[ngModel]="percentageOfInterestSelectedLabel"
				>
				</app-dropdown>

				<div
					class="grid grid-cols-1 gap-y-4 mt-2"
					*ngIf="
						_mortgage.percentageOfInterestType ===
						percentageOfInterestTypes.CAPACITY_PER_CLIENT
					"
				>
					<app-lender-percentage-of-interest
						*ngFor="let lender of lenders; trackBy: trackLenderById"
						[lender]="lender"
						(onLenderPercentageOfInterestDetailsUpdated)="
							onLenderPercentageOfInterestDetailsUpdated.emit({
								lenderId: lender.id,
								percentageOfInterest: $event.percentage,
								percentageOfInterestType: $event.type,
							})
						"
					></app-lender-percentage-of-interest>
				</div>
			</app-container>

			<app-container
				type="clear"
				[padding]="false"
				[border]="false"
				*ngIf="lenders?.length > 0"
			>
				<div class="relative">
					<div class="flex justify-between my-4">
						<h5 class="mb-0 font-bold">Correspondence details</h5>
						<app-radio-group
							type="compact"
							[items]="correspondenceTypeOptions"
							[ngModel]="_mortgage?.correspondence?.correspondenceType"
							(ngModelChange)="setCorrespondenceType($event)"
						></app-radio-group>
					</div>
					<app-solicitor
						*ngIf="
							_mortgage?.correspondence?.correspondenceType ===
							correspondenceTypes.SOLICITOR
						"
						[solicitor]="_mortgage?.correspondence?.correspondence"
						[showClerk]="true"
						(onUpdate)="onCorrespondenceSolicitorUpdate.emit($event)"
					></app-solicitor>
				</div>
			</app-container>
		</div>
	</div>
</ui-section>
